import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { BookingStatusPill } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Mail, MessageSquare, Phone, Send } from "lucide-react";

function fmtLong(d) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function waLink(mobile, text) {
  const digits = (mobile || "").replace(/\D/g, "");
  if (!digits) return null;
  const intl = digits.startsWith("0") ? "44" + digits.slice(1) : digits;
  return `https://wa.me/${intl}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
}

const CHANNEL_STYLES = {
  portal: "text-sky-800 bg-sky-100 border-sky-300",
  email: "text-purple-800 bg-purple-100 border-purple-300",
  whatsapp: "text-emerald-800 bg-emerald-100 border-emerald-300",
  call: "text-slate-700 bg-slate-100 border-slate-300",
  system_reminder: "text-amber-800 bg-amber-100 border-amber-300",
};

export function AssessmentDayAdmin({ candidateId, summary, profile, onChanged }) {
  const [booking, setBooking] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [pickMode, setPickMode] = useState("book");
  const [events, setEvents] = useState([]);
  const [picked, setPicked] = useState("");

  const load = () => api.get(`/staff/candidates/${candidateId}/booking`)
    .then((r) => { setBooking(r.data?.booking || null); setLoaded(true); })
    .catch(() => setLoaded(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

  const openPicker = async (mode) => {
    const { data } = await api.get("/admin/events");
    setEvents(data.filter((e) => ["open", "full"].includes(e.effective_status) && e.id !== booking?.event_id));
    setPickMode(mode);
    setPicked("");
    setPickOpen(true);
  };

  const confirmPick = async (force = false) => {
    if (!picked) { toast.error("Choose an assessment day."); return; }
    try {
      if (pickMode === "book") {
        await api.post(`/admin/events/${picked}/book`, { candidate_id: candidateId, force });
        toast.success("Candidate booked — confirmation sent to their portal.");
      } else {
        await api.post(`/admin/bookings/${booking.id}/action`, { action: "reschedule", event_id: picked });
        toast.success("Candidate rescheduled — confirmation sent to their portal.");
      }
      setPickOpen(false);
      load();
      onChanged?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm("That assessment day is FULL. Override capacity and book anyway?")) return confirmPick(true);
      } else toast.error(apiError(e));
    }
  };

  const cancelBooking = async () => {
    if (!window.confirm("Cancel this booking? The slot is released immediately.")) return;
    try {
      await api.post(`/admin/bookings/${booking.id}/action`, { action: "cancel" });
      toast.success("Booking cancelled.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!loaded) return null;
  const ev = booking?.event || {};

  return (
    <section className="bg-white border border-steel rounded-md p-6" data-testid="assessment-day-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">
          <CalendarDays className="inline h-4 w-4 mr-1.5 -mt-0.5 text-lomond" />Assessment Day
        </h2>
        {booking ? <BookingStatusPill status={booking.status} /> : (
          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 border-slate-300" data-testid="no-booking-badge">
            {summary.stage === "invited" ? "Invited — not booked" : "Not invited"}
          </Badge>
        )}
      </div>
      {booking ? (
        <p className="text-sm text-charcoal" data-testid="booking-summary">
          {fmtLong(booking.event_date)} — {booking.event_start_time} — {booking.event_location}
          {ev.address ? <span className="text-slate-500"> — {ev.address}</span> : null}
        </p>
      ) : (
        <p className="text-sm text-slate-500">No assessment day booked for this candidate.</p>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        {!booking || ["cancelled", "no_show", "rescheduled", "declined_invite"].includes(booking.status) ? (
          <Button size="sm" onClick={() => openPicker("book")} className="bg-lomond text-black font-bold hover:bg-[#E6B400] h-8 text-xs" data-testid="admin-book-btn">Book</Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => openPicker("reschedule")} className="border-steel text-charcoal bg-white h-8 text-xs" data-testid="admin-reschedule-btn">Reschedule</Button>
            <Button size="sm" variant="outline" onClick={cancelBooking} className="border-red-300 text-red-700 bg-white hover:bg-red-50 h-8 text-xs" data-testid="admin-cancel-booking-btn">Cancel</Button>
          </>
        )}
        <a href={`mailto:${summary.email}`}><Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid="quick-email"><Mail className="h-3.5 w-3.5 mr-1" />Email</Button></a>
        {waLink(profile.mobile) && <a href={waLink(profile.mobile)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid="quick-whatsapp"><MessageSquare className="h-3.5 w-3.5 mr-1" />WhatsApp</Button></a>}
        {profile.mobile && <a href={`tel:${profile.mobile}`}><Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid="quick-call"><Phone className="h-3.5 w-3.5 mr-1" />Call</Button></a>}
      </div>

      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent className="bg-white border-steel text-foreground" data-testid="admin-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">{pickMode === "book" ? "Book Assessment Day" : "Reschedule"} — {summary.name}</DialogTitle>
          </DialogHeader>
          <Select value={picked} onValueChange={setPicked}>
            <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="admin-book-event-select"><SelectValue placeholder="Select assessment day" /></SelectTrigger>
            <SelectContent>
              {events.map((e) => (
                <SelectItem key={e.id} value={e.id} data-testid={`admin-book-option-${e.id}`}>
                  {fmtLong(e.date)} — {e.start_time} — {e.spaces_remaining > 0 ? `${e.spaces_remaining} spaces` : "FULL"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPickOpen(false)} className="border-steel text-charcoal bg-white" data-testid="admin-book-cancel">Cancel</Button>
            <Button onClick={() => confirmPick(false)} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="admin-book-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function CommsHub({ candidateId, summary, profile }) {
  const [timeline, setTimeline] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [channel, setChannel] = useState("portal");
  const [templateKey, setTemplateKey] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [prefs, setPrefs] = useState(profile.comms_prefs || { email: true, whatsapp: true, sms: false });
  const [bookingEvent, setBookingEvent] = useState(null);

  const load = () => api.get(`/staff/candidates/${candidateId}/communications`).then((r) => setTimeline(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get(`/staff/candidates/${candidateId}/booking`).then((r) => setBookingEvent(r.data?.booking?.event || null)).catch(() => {});
    // eslint-disable-next-line
  }, [candidateId]);

  const mergeCtx = () => ({
    first_name: summary.name.split(" ")[0] || "",
    last_name: summary.name.split(" ").slice(1).join(" "),
    assessment_date: bookingEvent ? fmtLong(bookingEvent.date) : "",
    assessment_time: bookingEvent ? `${bookingEvent.start_time}–${bookingEvent.end_time}` : "",
    location: bookingEvent?.location_name || "",
    address: bookingEvent?.address || "",
    contact_name: bookingEvent?.contact_name || "Lomond Academy",
    contact_number: bookingEvent?.contact_number || "",
    programme_name: "Lomond Operator Academy",
    arrival_minutes: String(bookingEvent?.arrival_minutes ?? 15),
  });

  const applyTemplate = (key) => {
    setTemplateKey(key);
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    const ctx = mergeCtx();
    const render = (s) => Object.entries(ctx).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), s);
    setSubject(render(tpl.subject || ""));
    setMessage(render(tpl.body || ""));
  };

  const send = async () => {
    if (!message.trim()) { toast.error("Write a message first."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/staff/candidates/${candidateId}/communications`, {
        channel, subject, message, event_id: bookingEvent?.id || null,
      });
      if (channel === "whatsapp" && data.wa_url) {
        window.open(data.wa_url, "_blank");
        toast.success("WhatsApp opened — recorded as INITIATED (delivery not confirmed).");
      } else if (channel === "email") {
        toast.success("Email recorded as LOGGED — no email provider connected yet.");
      } else {
        toast.success(channel === "call" ? "Call note logged." : "Message sent to candidate portal.");
      }
      setMessage(""); setSubject(""); setTemplateKey("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await api.put(`/staff/candidates/${candidateId}/comms-prefs`, next);
      toast.success("Contact preferences updated.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <section className="bg-white border border-steel rounded-md p-6" data-testid="comms-hub">
      <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Communications</h2>

      <div className="flex flex-wrap gap-4 mb-5 bg-elevated rounded-md p-3" data-testid="comms-prefs">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 self-center">Candidate allows:</span>
        {[["email", "Email"], ["whatsapp", "WhatsApp / mobile"], ["sms", "SMS"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-charcoal cursor-pointer" data-testid={`pref-${k}`}>
            <Checkbox checked={!!prefs[k]} onCheckedChange={(v) => savePrefs({ ...prefs, [k]: !!v })} /> {l}
          </label>
        ))}
      </div>

      <div className="bg-elevated rounded-md p-4 space-y-3 mb-6" data-testid="comms-composer">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-slate-600 text-xs uppercase tracking-wider">Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="comm-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="portal">Portal message</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp (wa.me)</SelectItem>
                <SelectItem value="call">Call note</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-slate-600 text-xs uppercase tracking-wider">Template (optional)</Label>
            <Select value={templateKey} onValueChange={applyTemplate}>
              <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="comm-template"><SelectValue placeholder="Choose a template to pre-fill" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`comm-template-${t.key}`}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {channel !== "call" && (
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
            className="bg-white border-steel text-charcoal" data-testid="comm-subject" />
        )}
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder={channel === "call" ? "Call notes — who you spoke to, what was agreed..." : "Message..."}
          className="bg-white border-steel text-charcoal min-h-24" data-testid="comm-message" />
        {channel === "whatsapp" && <p className="text-[10px] font-mono uppercase tracking-widest text-amber-700">WhatsApp opens in a new tab via wa.me — recorded as INITIATED, not delivered.</p>}
        {channel === "email" && <p className="text-[10px] font-mono uppercase tracking-widest text-purple-700">No email provider connected yet — emails are recorded as LOGGED.</p>}
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="comm-send-btn">
            <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending..." : channel === "call" ? "Log Call Note" : channel === "whatsapp" ? "Open WhatsApp & Log" : channel === "email" ? "Log Email" : "Send"}
          </Button>
        </div>
      </div>

      <div className="space-y-3" data-testid="comms-timeline">
        {timeline.map((c) => (
          <div key={c.id} className="border-l-2 border-lomond pl-3 py-1" data-testid={`comm-${c.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`font-mono text-[9px] uppercase tracking-widest ${CHANNEL_STYLES[c.channel] || CHANNEL_STYLES.portal}`}>
                {c.channel === "system_reminder" ? "SYSTEM REMINDER" : c.channel === "whatsapp" ? "WHATSAPP INITIATED" : c.channel}
              </Badge>
              {c.subject && <span className="text-xs font-semibold text-charcoal">{c.subject}</span>}
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                {c.at ? new Date(c.at).toLocaleString("en-GB") : ""} — {c.staff_name || "System"}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{c.message}</p>
          </div>
        ))}
        {!timeline.length && <p className="text-sm text-slate-500">No communications yet.</p>}
      </div>
    </section>
  );
}
